/**
 * Rótulos de negócio da tela «Risco por Ação» (D2).
 *
 * Traduzem o vocabulário NEUTRO do motor (ações, categorias, níveis, camadas) para a língua do
 * operador. O id canônico fica sempre no `title`/diagnóstico — nunca vira copy (mesma disciplina
 * do `BondTypeConfigsView`).
 *
 * ⚠️ Estes rótulos NÃO reintroduzem sabor setorial: as 17 ações são universais (é a tese do
 * programa). «Fatura», «serviço» e afins descrevem a CLASSE de ação, não um vertical.
 */

import type { CategorySource, IdentityAssuranceLevel, RiskCategory } from './types';

const ACTION_LABELS: Record<string, string> = {
  public_info: 'Informação pública',
  account_context: 'Contexto da conta',
  sensitive_data_read: 'Leitura de dado sensível',
  billing_payment_code_read: 'Código de pagamento',
  service_status_read: 'Situação do serviço',
  service_connection_read: 'Estado da conexão',
  service_ticket_read: 'Chamado de serviço',
  cadastral_change: 'Alteração cadastral',
  service_slots_query: 'Consulta de janelas de serviço',
  service_action: 'Ação sobre o serviço',
  appointment_confirm: 'Confirmação de compromisso',
  agenda_availability_query: 'Consulta de disponibilidade',
  agenda_book: 'Agendamento',
  agenda_reschedule: 'Reagendamento',
  agenda_cancel: 'Cancelamento de agendamento',
  agenda_join_waitlist: 'Entrada em lista de espera',
  out_of_scope: 'Fora de escopo',
};

/** Famílias — agrupam as 17 ações por natureza, no molde das 5 famílias de Bond. */
export const ACTION_FAMILIES: readonly { id: string; label: string; actions: readonly string[] }[] =
  [
    {
      id: 'leitura',
      label: 'Leitura de dados',
      actions: [
        'public_info',
        'account_context',
        'sensitive_data_read',
        'billing_payment_code_read',
      ],
    },
    {
      id: 'servico',
      label: 'Serviço',
      actions: [
        'service_status_read',
        'service_connection_read',
        'service_ticket_read',
        'service_slots_query',
        'service_action',
      ],
    },
    { id: 'cadastro', label: 'Cadastro', actions: ['cadastral_change'] },
    {
      id: 'agenda',
      label: 'Agenda',
      actions: [
        'agenda_availability_query',
        'agenda_book',
        'agenda_reschedule',
        'agenda_cancel',
        'agenda_join_waitlist',
        'appointment_confirm',
      ],
    },
    { id: 'fronteira', label: 'Fronteira', actions: ['out_of_scope'] },
  ];

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function categoryLabel(category: RiskCategory): string {
  switch (category) {
    case 'none':
      return 'Sem risco';
    case 'read-pii':
      return 'Leitura de dado pessoal';
    case 'write-financial':
      return 'Escrita financeira';
    case 'irreversible':
      return 'Irreversível';
    case 'regulated-data':
      return 'Dado regulado';
  }
}

export function assuranceLabel(level: IdentityAssuranceLevel): string {
  switch (level) {
    case 'none':
      return 'Sem identificação';
    case 'self_declared':
      return 'Auto-declarado';
    case 'verified':
      return 'Titular verificado';
  }
}

export function sourceLabel(source: CategorySource): string {
  switch (source) {
    case 'tenant':
      return 'Definido por você';
    case 'vertical':
      return 'Definido para o seu segmento';
    case 'platform-seed':
      return 'Padrão da plataforma';
    case 'conservative-default':
      return 'Padrão conservador';
  }
}

export function verticalLabel(vertical: string | null): string {
  if (vertical === null) return 'Sem vertical (base)';
  const labels: Record<string, string> = {
    support: 'Suporte',
    commercial: 'Comercial',
    retention: 'Retenção',
    collections: 'Cobrança',
    triage: 'Triagem',
    onboarding: 'Onboarding',
    satisfaction_survey: 'Pesquisa de satisfação',
    legal: 'Jurídico',
    healthcare: 'Saúde',
    other: 'Outro',
  };
  return labels[vertical] ?? vertical;
}
